import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import Box from 'components/atoms/Layout/Box/Box';
import Typography, {P} from 'components/atoms/Text/Typography/Typography';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import Tab from 'components/atoms/Tabs/Tab/Tab';
import Tabs from 'components/atoms/Tabs/Tabs/Tabs';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Icon from 'components/atoms/Icons/Icon/Icon';
import StyledEntityPatentGraphProfileCardContent
  from 'components/organisms/Cards/EntityPatentGraphProfileCardContent/EntityPatentGraphProfileCardContent.styles';
import {useClientStatisticCpcBreakdown} from 'services/client/statistic/statistic.utils';
import Link from 'components/atoms/Links/Link/Link';
import PatentBarChart from 'components/organisms/Charts/PatentBarChart/PatentBarChart';

const EntityPatentGraphProfileCardContent = React.forwardRef((props, ref) => {
  const {
    card,
    content,
    entity,
    isLoading,
    ...innerProps
  } = useComponentProps(props, 'EntityPatentGraphProfileCardContent', {
    static: ['isEditing']
  });

  const innerRef = useRef(null);
  const [breakdown, setBreakdown] = useState(null);
  const [internalState, setInternalState] = useState({
    level: constants.data.lookup('patentLevels', constants.entity.patent.levels.section),
    period: null,
    activeTabIdx: 0
  })

  useImperativeHandle(ref, () => innerRef.current);

  const years = constants.data.patentPeriods.reduce((m, p) => Math.max(m, p.years), 0);

  const [cpcBreakdown, isBreakdownLoading] = useClientStatisticCpcBreakdown(
    years,
    [{id: 'entityIds', value: [entity?.entityId]}],
    {
      enabled: entity?.entityId > 0
    }
  );

  const isEditing = content.state.isEditing;
  const isDataLoading = isLoading || isBreakdownLoading;

  useEffect(() => {
    if (cpcBreakdown) {
      setBreakdown(cpcBreakdown);
    }
  }, [cpcBreakdown]);

  const fields = useMemo(() => {
    const initialOption = constants.data.patentLevels.find((opt) => opt.value === internalState.level.value);

    return [{
      name: 'type',
      type: constants.formFieldTypes.autocomplete,
      validation: constants.formFieldValidationTypes.text,
      placeholder: 'Select a level',
      initial: initialOption,
      FormFieldProps: {
        autoFocus: false,
        hiddenLabel: true,
        clearable: false,
        size: 'smaller'
      },
      options: constants.data.patentLevels
    }];
  }, [internalState.level]);

  useEffect(() => {
    let periodIdx = constants.data.patentPeriods.findIndex((period) => {
      const from = new Date(`${(new Date()).toISOString().split('-').slice(0, 1).join('-')}-01-01T00:00:00.000Z`);
      from.setYear(from.getFullYear() - (period.years - 1));

      return Boolean(!breakdown?.some((bd) => {
        return bd.cpcs?.length > 0 && from.getTime() >
          (new Date(`${bd.filingYear}-01-01T00:00:00.000Z`)).getTime();
      }));
    });

    periodIdx = periodIdx !== -1 ? periodIdx : constants.data.patentPeriods.length - 1;
    setInternalState(utils.updater({
      activeTabIdx: periodIdx,
      period: constants.data.patentPeriods[periodIdx]
    }, true));
  }, [breakdown]);

  const handleLevelChange = useCallback((field, value) => {
    setInternalState(utils.updater({level: value}, true));
  }, []);

  const handleTabChange = (e, value) => {
    setInternalState(utils.updater({
      activeTabIdx: value,
      period: constants.data.patentPeriods[value]
    }, true));
  };

  innerProps.className = utils.flattenClassName(innerProps.className, {
    isEditing: isEditing
  });

  return <StyledEntityPatentGraphProfileCardContent ref={innerRef} {...innerProps}>
    <InlineForm className="EntityPatentGraphProfileCardContent-type"
                onChange={handleLevelChange}
                fields={fields} />
    {(breakdown?.filter((bd) => bd.cpcs?.length > 0)?.length > 0 || isDataLoading) ? <React.Fragment>
      <PatentBarChart className="EntityPatentGraphProfileCardContent-chart"
                      patents={breakdown}
                      isLoading={isDataLoading}
                      level={internalState.level}
                      period={internalState.period}/>
      <Tabs className="EntityPatentGraphProfileCardContent-tabs"
            size="small"
            density="dense"
            value={internalState.activeTabIdx ?? 0}
            onChange={handleTabChange}>
        {constants.data.patentPeriods.map((p, idx) => {
          return <Tab key={idx} value={idx} label={p.label}/>
        })}
      </Tabs>
      <Box className="EntityPatentGraphProfileCardContent-info">
        <Icon icon={InfoOutlined} color="primary" size="smaller"/>
        <Typography variant="caption" color="text.secondary" showTooltip={true} TooltipProps={{enterDelay: 0}}>
          Patent information is based on <Link href={utils.cleanExternalLink('https://worldwide.espacenet.com')}
                                               target="_blank">Espacenet</Link> data
        </Typography>
      </Box>
    </React.Fragment> : null}
    {(!(breakdown?.filter((bd) => bd.cpcs?.length > 0)?.length > 0) && !isDataLoading) ?
      <Box className="EntityPatentGraphProfileCardContent-empty">
        <P>No patent data found</P>
      </Box> : null}
  </StyledEntityPatentGraphProfileCardContent>
});

EntityPatentGraphProfileCardContent.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  card: PropTypes.object,
  content: PropTypes.object,
  entity: PropTypes.object,
  isLoading: PropTypes.bool
};

export default EntityPatentGraphProfileCardContent;


